import moment from "moment";
import React, { Component } from "react";
import { FormattedMessage, injectIntl, type IntlShape, type MessageDescriptor } from "react-intl";
import { connect, type ConnectedProps } from "react-redux";

import { assertIsUser } from "~/common/user";
import { getBonuses, selectBonuses } from "~/slices/bonuses";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";

import BarChartHorizontal from "../charts/bar_chart_horizontal";

const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    bonuses: selectBonuses(state),
  };
};

const mapDispatchToProps = {
  getBonuses,
};

type PatientBonusesProps = PropsFromRedux & { intl: IntlShape };

class PatientBonuses extends Component<PatientBonusesProps> {
  constructor(props: PatientBonusesProps) {
    super(props);
  }

  componentDidMount() {
    assertIsUser(this.props.user);
    void this.props.getBonuses(this.props.user.account_id);
  }

  renderFullCoins(available: number) {
    const arr = [];

    for (let i = 0; i < available; i++) {
      arr.push(
        <div key={i} className="tip" style={{ width: 70, color: "black" }}>
          <span>{this.props.intl.formatMessage({ id: "bonus.full.coin.title" })}</span>
          <img src="/img/10_tiffani.svg" alt="" />
        </div>,
      );
    }

    return arr;
  }

  renderSemiCoins(used: number) {
    const arr = [];

    for (let i = 0; i < used; i++) {
      arr.push(
        <div key={i} className="tip" style={{ width: 70 }}>
          <span>{this.props.intl.formatMessage({ id: "bonus.case.chosen" })}</span>
          <img src="/img/10_grey.svg" alt="" />
        </div>,
      );
    }

    return arr;
  }

  renderColoredCoins(current: number, target: number) {
    const { intl } = this.props;

    return (
      <div className="tip" style={{ width: "70px" }}>
        <img src="/img/10_tiffani_stroke.svg" alt="" />
        <span>
          {intl.formatMessage({ id: "bonus.cases.left" })} {current}{" "}
          {intl.formatMessage({ id: "bonus.cases.left.1" })} {target}{" "}
          {intl.formatMessage({ id: "bonus.cases.left.2" })}
        </span>
      </div>
    );
  }

  currentMessage(current: number) {
    const { locale } = this.props.intl;
    let translationId: MessageDescriptor["id"] = "bonus.patient.activation";

    if (locale == "ru") {
      if (current == 1) {
        translationId = "bonus.patient.activation.one";
      } else if (current >= 2 && current < 5) {
        translationId = "bonus.patient.activation.more";
      } else {
        translationId = "bonus.patient.activation.a.lot";
      }
    } else {
      if (current == 1) {
        translationId = "bonus.patient.activation.one";
      } else {
        translationId = "bonus.patient.activation.more";
      }
    }

    return this.props.intl.formatMessage({ id: translationId });
  }

  render() {
    const { bonuses, intl } = this.props;

    if (!bonuses) {
      return null;
    }

    const current = bonuses.target - bonuses.current;

    return (
      <div>
        <div className="caption">
          {bonuses.start != null && bonuses.end != null ? (
            <img src="/img/calendar.svg" style={{ maxWidth: 20, maxHeight: 20 }} alt="" />
          ) : null}

          {bonuses.start != null && bonuses.end != null ? (
            <span style={{ marginLeft: "1rem", position: "relative", top: ".2rem" }}>
              {intl.locale == "ru"
                ? `${moment(bonuses.start, "YYYY-MM-DD", "en")
                    .locale("ru")
                    .format("DD MMMM YYYY")} - ${moment(bonuses.end, "YYYY-MM-DD", "en")
                    .locale("ru")
                    .format("DD MMMM YYYY")}`
                : `${moment(bonuses.start, "YYYY-MM-DD", "en")
                    .locale("en")
                    .format("MMMM DD, YYYY")} - ${moment(bonuses.end, "YYYY-MM-DD", "en")
                    .locale("en")
                    .format("MMMM DD, YYYY")}`}
            </span>
          ) : null}
        </div>

        {bonuses.current > 0 ? (
          <div
            className="portlet-title"
            style={{
              position: "relative",
              width: "80%",
              paddingBottom: "100px",
              marginLeft: "-25px",
            }}
          >
            <div
              style={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                top: 0,
              }}
            >
              <BarChartHorizontal data={[{ ...bonuses }]} />
            </div>
          </div>
        ) : null}

        <div className="row" style={{ marginTop: "-15px", marginLeft: "10px" }}>
          <div className="col-md-3">
            <div className="bonus">
              <div className="tip">
                <h1 className="caption-subject font-green bold">
                  {bonuses.months != null
                    ? bonuses.months
                    : bonuses.days != null && bonuses.days > 0
                    ? bonuses.days
                    : null}
                </h1>

                <span>
                  {bonuses.months != null
                    ? intl.formatMessage({ id: "bonus.period.end.months" })
                    : intl.formatMessage({ id: "bonus.period.end.days" })}
                </span>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="bonus">
              <div className="tip">
                <h1 className="caption-subject font-green bold">{current > 0 ? current : null}</h1>
                <span>{this.currentMessage(current)}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="row" style={{ marginLeft: 10 }}>
          <div className="col-md-8">
            <div className="bonus">
              {bonuses.used > 0 ? this.renderSemiCoins(bonuses.used) : null}
              {bonuses.available > 0 ? this.renderFullCoins(bonuses.available) : null}
              {bonuses.current > 0
                ? this.renderColoredCoins(bonuses.current, bonuses.target)
                : null}
            </div>
          </div>
        </div>

        <div className="row" style={{ marginLeft: 10 }}>
          <div className="col-md-6">
            <div className="bonus">
              <div style={{ width: "3rem" }}>
                <img src="/img/client.svg" alt="" />
              </div>

              <div
                className="caption-subject bold uppercase"
                style={{ position: "relative", bottom: "-5px" }}
              >
                <FormattedMessage id="bonus.patients.total" /> {bonuses.total}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientBonuses));
